import { useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notice } from '@dxtech/shared';
import { api } from '../api/client.js';
import { useApp } from '../state/app.js';
import { Spinner } from '../ui/common.js';
import { NoticeFormModal } from '../modals/NoticeFormModal.js';
import { NoticeDetailModal } from '../modals/NoticeDetailModal.js';

function dot(d?: string): string { return d ? d.replace(/-/g, '.') : ''; }

/** 개인 메인화면 상단 공지사항 위젯 — 오늘 이후 게시 목록. 클릭 시 상세 레이어드 팝업. */
export function NoticeBoard(): JSX.Element {
  const { me, flash } = useApp();
  const qc = useQueryClient();
  const isAdmin = me.roles.includes('Admin');
  const notices = useQuery({ queryKey: ['notices', 'upcoming'], queryFn: () => api.notices('upcoming') });

  const [detailId, setDetailId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Notice | null>(null);

  const items = notices.data?.items ?? [];

  const openCreate = (): void => { setEditing(null); setFormOpen(true); };
  const openEdit = (n: Notice): void => { setDetailId(null); setEditing(n); setFormOpen(true); };

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '16px 18px 13px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>공지사항</div>
        <span style={{ fontSize: 11.5, color: '#9CA3AF', fontWeight: 500 }}>총 {items.length}건</span>
        {isAdmin && (
          <button className="dx-btn dx-btn-primary" style={{ marginLeft: 'auto', fontSize: 13, padding: '6px 12px' }} onClick={openCreate}>공지 등록</button>
        )}
      </div>

      {notices.isLoading ? (
        <div style={{ padding: 32, textAlign: 'center' }}><Spinner /></div>
      ) : items.length === 0 ? (
        <div style={{ padding: 28, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>표시할 공지가 없습니다.</div>
      ) : (
        <div>
          {items.map((n) => (
            <button key={n.id} onClick={() => setDetailId(n.id)}
              className="dx-notice-row"
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px', border: 'none', borderBottom: '1px solid #F3F4F6', background: '#fff', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
              {n.pinned && <span style={{ fontSize: 10.5, fontWeight: 700, color: '#B45309', background: '#FEF3C7', borderRadius: 5, padding: '2px 6px', flexShrink: 0 }}>고정</span>}
              {!n.readByMe && <span title="읽지 않음" style={{ width: 7, height: 7, borderRadius: '50%', background: '#2563EB', flexShrink: 0 }} />}
              <span style={{ fontSize: 13.5, fontWeight: n.readByMe ? 500 : 700, color: '#1F2937', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
              {isAdmin && <span style={{ fontSize: 11.5, color: '#9CA3AF', flexShrink: 0 }}>열람 {n.readCount ?? 0}</span>}
              <span className="tnum" style={{ fontSize: 12, color: '#9CA3AF', flexShrink: 0 }}>{dot(n.noticeDate)}</span>
            </button>
          ))}
        </div>
      )}

      {detailId && (
        <NoticeDetailModal
          id={detailId} isAdmin={isAdmin}
          onClose={() => { setDetailId(null); qc.invalidateQueries({ queryKey: ['notices'] }); }}
          onEdit={openEdit}
          onDone={(m) => flash(m)}
        />
      )}
      {formOpen && (
        <NoticeFormModal
          notice={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onDone={(m) => flash(m)}
        />
      )}
    </div>
  );
}
